// The front camera, opened once after the first unlock and kept warm (plan §6 step 1), and the
// still photo taken from it: 640 px wide JPEG at quality 0.7 (plan D5, about 40-80 KB).
// Injected into the flow as a `Camera` so the teacher app can pass its phone camera instead.

export interface Photo { bytes: Uint8Array; width: number; height: number; /** photo px per video px */ scale: number }

export interface Camera {
  /** The live stream for previews; null when no camera is available. */
  stream: MediaStream | null;
  /** A playing video element fed by the stream: detection and capture read frames from it. */
  video: HTMLVideoElement | null;
  error: string | null;
  capture(): Promise<Photo | null>;
}

export const PHOTO_WIDTH = 640;
export const PHOTO_QUALITY = 0.7;

/** The front camera by default; the guardian app scans door codes with the back one. */
export async function openCamera(facing: "user" | "environment" = "user"): Promise<Camera> {
  const none = (error: string): Camera => ({ stream: null, video: null, error, capture: async () => null });
  if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) return none("This device has no camera access.");
  let stream: MediaStream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      audio: false, video: { facingMode: facing, width: { ideal: 1280 }, height: { ideal: 720 } },
    });
  } catch (e) {
    return none((e as Error).name === "NotAllowedError" ? "Camera permission is off." : "The camera could not be opened.");
  }
  const video = document.createElement("video");
  video.muted = true;
  video.playsInline = true;
  video.srcObject = stream;
  await video.play().catch(() => undefined);
  return { stream, video, error: null, capture: () => grab(video) };
}

export async function grab(video: HTMLVideoElement, width = PHOTO_WIDTH, quality = PHOTO_QUALITY): Promise<Photo | null> {
  const vw = video.videoWidth, vh = video.videoHeight;
  if (!vw || !vh) return null;
  const scale = Math.min(1, width / vw);
  const w = Math.round(vw * scale), h = Math.round(vh * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.drawImage(video, 0, 0, w, h);
  const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, "image/jpeg", quality));
  if (!blob) return null;
  return { bytes: new Uint8Array(await blob.arrayBuffer()), width: w, height: h, scale };
}

export function stopCamera(c: Camera | null) {
  c?.stream?.getTracks().forEach((t) => t.stop());
}
